import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  DocumentIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import { downloadCertificatePDF } from '../../utils/pdfService';

const CertificatePreviewModal = ({ certification, onClose }) => {
  const [downloadState, setDownloadState] = useState('idle'); // idle, downloading, success, error
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [customTemplate, setCustomTemplate] = useState(null);
  const [templateImage, setTemplateImage] = useState(null);

  // Charger le template personnalisé au montage
  useEffect(() => {
    const loadCustomTemplate = async () => {
      const activeTemplateId = localStorage.getItem('activeCertificationTemplate');
       
      if (activeTemplateId) {
        const templates = JSON.parse(localStorage.getItem('certificationTemplates') || '[]');
        const activeTemplate = templates.find(t => t.id === activeTemplateId);
        
        if (activeTemplate && activeTemplate.data) {
          setCustomTemplate(activeTemplate);
          
          // Convertir le template en image pour la preview
          const imageData = await convertPdfToImageForPreview(activeTemplate.data);
          if (imageData) {
            setTemplateImage(imageData);
          }
        }
      }
    };
    
    loadCustomTemplate();
  }, []);

  // Convertir PDF en image pour la preview
  const convertPdfToImageForPreview = async (base64Data) => {
    try {
       // Vérifier que PDF.js est disponible
       if (!window.pdfjsLib) {
         console.error('PDF.js not loaded');
        return null;
      }

       // Configuration du worker
       if (window.pdfjsLib.GlobalWorkerOptions) {
         window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
       }

       // Convertir base64 en array buffer
       let pdfData;
       if (base64Data.startsWith('data:')) {
         // Si c'est un data URI complet
         const base64String = base64Data.split(',')[1];
         const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
         }
         pdfData = bytes;
       } else {
         // Si c'est juste du base64
         const binaryString = atob(base64Data);
         const bytes = new Uint8Array(binaryString.length);
         for (let i = 0; i < binaryString.length; i++) {
           bytes[i] = binaryString.charCodeAt(i);
         }
         pdfData = bytes;
       }

       // Charger le PDF
       const loadingTask = window.pdfjsLib.getDocument({
         data: pdfData,
         cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
         cMapPacked: true,
         verbosity: 0
       });

       const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);

       // Créer le canvas
       const scale = 1.2; // Réduire un peu pour éviter les problèmes de mémoire
      const viewport = page.getViewport({ scale });
       
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;

       // Rendre la page
      await page.render({
        canvasContext: context,
         viewport: viewport,
         enableWebGL: false // Désactiver WebGL pour éviter les problèmes
      }).promise;

       // Convertir en image
       const imageData = canvas.toDataURL('image/png', 0.8); // Réduire la qualité pour optimiser
       
       return imageData;
    } catch (error) {
       console.error('Erreur conversion PDF:', error);
       
       // Fallback: utiliser le template comme image de base si possible
       try {
         if (base64Data.startsWith('data:application/pdf')) {
           // Retourner une image placeholder qui indique qu'on utilise le template
           return 'data:image/svg+xml;base64,' + btoa(`
             <svg width="297" height="210" xmlns="http://www.w3.org/2000/svg">
               <rect width="100%" height="100%" fill="#f0f0f0"/>
               <text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="Arial" font-size="16" fill="#666">
                 Template PDF chargé
               </text>
               <text x="50%" y="60%" text-anchor="middle" dy=".3em" font-family="Arial" font-size="12" fill="#999">
                 (Aperçu simplifié - Le PDF final utilisera votre template)
               </text>
             </svg>
           `);
         }
       } catch (fallbackError) {
         console.error('Fallback failed:', fallbackError);
       }
       
      return null;
    }
  };

  // Simulation de chargement pour une meilleure expérience utilisateur
  useEffect(() => {
    if (downloadState === 'downloading') {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 95) {
            clearInterval(interval);
            return 95;
          }
          return prev + Math.random() * 15;
        });
      }, 150);
      
      // Simulation de fin de téléchargement après un délai
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setLoadingProgress(100);
        setDownloadState('success');
      }, 1500);
      
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [downloadState]);

  // Gestion du téléchargement
  const handleDownload = async () => {
    try {
      setDownloadState('downloading');
      setLoadingProgress(0);
      
      // Générer et télécharger le PDF
      setTimeout(async () => {
        const success = await downloadCertificatePDF(certification);
        if (!success) {
          setDownloadState('error');
        }
      }, 500);
    } catch (error) {
      setDownloadState('error');
    }
  };

  // Gestion de l'impression
  const handlePrint = async () => {
    try {
      // Vérifier s'il y a un template personnalisé
      const activeTemplateId = localStorage.getItem('activeCertificationTemplate');
      let printContent;

      if (activeTemplateId) {
        const templates = JSON.parse(localStorage.getItem('certificationTemplates') || '[]');
        const activeTemplate = templates.find(t => t.id === activeTemplateId);
        
        if (activeTemplate && activeTemplate.data) {
          printContent = await generatePrintContentWithTemplate(certification, activeTemplate, certificateTypeLabel);
        } else {
          printContent = generateDefaultPrintContent(certification, certificateTypeLabel);
        }
      } else {
        printContent = generateDefaultPrintContent(certification, certificateTypeLabel);
      }

      // Créer une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Attendre que le contenu soit chargé puis imprimer
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      };
      
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      alert('Erreur lors de l\'impression. Veuillez réessayer.');
    }
  };

  // Générer le contenu d'impression avec template personnalisé
  const generatePrintContentWithTemplate = async (cert, template, typeLabel) => {
    try {
      const imageData = await convertPdfToImageForPrint(template.data);
      
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificat - ${cert.studentName}</title>
          <style>
            @page { size: landscape; margin: 0; }
            @media print { 
              body { margin: 0; font-family: Arial, sans-serif; }
              .no-print { display: none !important; }
            }
            .certificate-container {
              position: relative;
              width: 297mm;
              height: 210mm;
              overflow: hidden;
            }
            .template-background {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              z-index: 1;
            }
            .data-overlay {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              z-index: 2;
            }
            .student-name {
              position: absolute;
              top: 45%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-size: 36px;
              font-weight: bold;
              color: #1e40af;
              text-align: center;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
            }
            .certificate-title {
              position: absolute;
              top: 60%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-size: 24px;
              font-weight: bold;
              color: #1e40af;
              text-align: center;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
            }
            .issue-date {
              position: absolute;
              top: 80%;
              left: 25%;
              transform: translate(-50%, -50%);
              font-size: 12px;
              color: #374151;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
            }
            .instructor {
              position: absolute;
              top: 80%;
              left: 75%;
              transform: translate(-50%, -50%);
              font-size: 12px;
              color: #374151;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
            }
            .expiration {
              position: absolute;
              top: 75%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-size: 12px;
              color: ${cert.expirationDate === 'never' || !cert.expirationDate ? '#22c55e' : '#374151'};
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
              font-weight: ${cert.expirationDate === 'never' || !cert.expirationDate ? 'bold' : 'normal'};
            }
            .certificate-id {
              position: absolute;
              top: 95%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-size: 8px;
              color: #9ca3af;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
            }
            ${cert.score ? `
            .score {
              position: absolute;
              top: 30%;
              left: 85%;
              transform: translate(-50%, -50%);
              font-size: 16px;
              font-weight: bold;
              color: #1e40af;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
            }
            ` : ''}
          </style>
        </head>
        <body>
          <div class="certificate-container">
            ${imageData ? `<img src="${imageData}" class="template-background" />` : ''}
            <div class="data-overlay">
              <div class="student-name">${cert.studentName}</div>
              <div class="certificate-title">${cert.certificateName}</div>
              <div class="issue-date">Délivré le: ${cert.issueDate ? new Date(cert.issueDate).toLocaleDateString('fr-FR') : 'Non définie'}</div>
              <div class="instructor">Formateur: ${cert.instructorName}</div>
              <div class="expiration">
                ${cert.expirationDate === 'never' || !cert.expirationDate 
                  ? '∞ Valide à vie' 
                  : `Expire le: ${new Date(cert.expirationDate).toLocaleDateString('fr-FR')}`
                }
              </div>
              ${cert.score ? `<div class="score">Score: ${cert.score}/100</div>` : ''}
              <div class="certificate-id">ID: ${cert.id} · Généré par FORMAPRO</div>
            </div>
          </div>
        </body>
        </html>
      `;
    } catch (error) {
      return generateDefaultPrintContent(cert, typeLabel);
    }
  };

  // Convertir PDF en image pour l'impression
  const convertPdfToImageForPrint = async (base64Data) => {
    try {
      const pdfjsLib = window.pdfjsLib;
      
      if (!pdfjsLib) {
        return null;
      }

      if (pdfjsLib.GlobalWorkerOptions) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      }

      const binaryString = atob(base64Data.split(',')[1]);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
      const page = await pdf.getPage(1);

      const scale = 3; // Haute résolution pour l'impression
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      return canvas.toDataURL('image/png');
    } catch (error) {
      return null;
    }
  };

  // Générer le contenu d'impression par défaut
  const generateDefaultPrintContent = (cert, typeLabel) => {
    return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificat - ${certification.studentName}</title>
          <style>
            @page { size: landscape; margin: 15mm; }
            @media print { 
              body { margin: 0; font-family: Arial, sans-serif; }
              .no-print { display: none !important; }
            }
            .certificate {
              width: 100%;
              max-width: 800px;
              margin: 0 auto;
              padding: 30px;
              border: 3px solid #2563eb;
              background: white;
              text-align: center;
              page-break-inside: avoid;
            }
            .header {
              background: linear-gradient(135deg, #2563eb, #7c3aed);
              color: white;
              padding: 20px;
              margin: -30px -30px 30px -30px;
              font-size: 24px;
              font-weight: bold;
              letter-spacing: 2px;
            }
            .student-name {
              font-size: 36px;
              color: #1e40af;
              font-weight: bold;
              margin: 20px 0;
              text-decoration: underline;
              text-decoration-color: #fbbf24;
            }
            .certificate-title {
              font-size: 24px;
              color: #1e40af;
              font-weight: bold;
              margin: 15px 0;
            }
            .details {
              display: flex;
              justify-content: space-around;
              margin: 40px 0;
              text-align: left;
            }
            .detail-item {
              flex: 1;
              padding: 0 20px;
            }
            .detail-label {
              font-weight: bold;
              color: #374151;
              margin-bottom: 5px;
            }
            .detail-value {
              color: #1f2937;
            }
            .signatures {
              display: flex;
              justify-content: space-between;
              margin-top: 60px;
              padding: 0 50px;
            }
            .signature-line {
              border-top: 1px solid #9ca3af;
              padding-top: 5px;
              width: 150px;
              text-align: center;
              font-size: 12px;
              color: #6b7280;
            }
            .seal {
              position: absolute;
              right: 50px;
              bottom: 80px;
              width: 80px;
              height: 80px;
              border: 3px solid #2563eb;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              background: white;
              font-weight: bold;
              color: #2563eb;
            }
          </style>
        </head>
        <body>
          <div class="certificate">
            <div class="header">
              ${certificateTypeLabel.toUpperCase()} - FORMAPRO
            </div>
            
            <p style="margin: 20px 0; color: #6b7280; font-size: 16px;">
              Ce document certifie que
            </p>
            
            <div class="student-name">
              ${certification.studentName}
            </div>
            
            <p style="margin: 20px 0; color: #6b7280; font-size: 16px;">
              a complété avec succès
            </p>
            
            <div class="certificate-title">
              ${certification.certificateName}
            </div>
            
            <div class="details">
              <div class="detail-item">
                <div class="detail-label">Date de délivrance:</div>
                <div class="detail-value">
                  ${certification.issueDate ? new Date(certification.issueDate).toLocaleDateString('fr-FR') : 'Non définie'}
                </div>
              </div>
              
              <div class="detail-item">
                <div class="detail-label">Formateur:</div>
                <div class="detail-value">${certification.instructorName}</div>
              </div>
              
              <div class="detail-item">
                <div class="detail-label">Date d'expiration:</div>
                <div class="detail-value">
                  ${certification.expirationDate === 'never' || !certification.expirationDate 
                    ? '∞ Valide à vie' 
                    : new Date(certification.expirationDate).toLocaleDateString('fr-FR')
                  }
                </div>
              </div>
              
              ${certification.score ? `
                <div class="detail-item">
                  <div class="detail-label">Score:</div>
                  <div class="detail-value">${certification.score}/100</div>
                </div>
              ` : ''}
            </div>
            
            <div class="signatures">
              <div class="signature-line">Signature du Formateur</div>
              <div class="signature-line">Direction</div>
            </div>
            
            <div class="seal">
              <div>
                <div style="font-size: 12px;">VALIDÉ</div>
                <div style="font-size: 10px;">${new Date().getFullYear()}</div>
              </div>
            </div>
            
            <div style="margin-top: 40px; font-size: 10px; color: #9ca3af; text-align: center;">
              ID: ${certification.id} · Généré par FORMAPRO
            </div>
          </div>
        </body>
        </html>
      `;
  };

  // Type de certificat en français
  const certificateTypeLabel = 
    certification.certificateType === 'diploma' ? 'Diplôme' :
    certification.certificateType === 'attestation' ? 'Attestation' : 'Certification';

  // Couleurs thématiques selon le type
  const typeColors = {
    diploma: 'from-purple-500 to-purple-700',
    certification: 'from-blue-500 to-blue-700',
    attestation: 'from-green-500 to-green-700'
  };

  const themeColor = typeColors[certification.certificateType] || typeColors.certification;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto">
        {/* Overlay avec flou */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Contenu du modal avec animation */}
        <div className="w-full max-w-3xl p-4 pt-8 pb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-2xl p-6 w-full z-10 shadow-2xl border border-slate-200 dark:border-slate-600 relative"
        >
          {/* En-tête avec bouton de fermeture */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 flex items-center">
              <DocumentIcon className="h-6 w-6 mr-2 text-slate-700 dark:text-slate-300" />
              {certificateTypeLabel} - {certification.id}
            </h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-full transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-slate-900 dark:text-slate-50" />
            </button>
          </div>
          
          {/* Contenu principal */}
          <div className="space-y-6">
            {/* Aperçu du certificat */}
            <div className={`bg-gradient-to-r ${themeColor} p-1 rounded-xl shadow-lg`}>
              <div className="bg-white rounded-lg overflow-hidden relative">
                 {customTemplate ? (
                   templateImage ? (
                  // Preview avec template personnalisé
                  <div className="relative w-full" style={{ aspectRatio: '297/210' }}>
                    <img 
                      src={templateImage} 
                      alt="Template" 
                      className="w-full h-full object-cover rounded-lg"
                    />
                       {/* Overlay des données - Positioning exact comme dans le PDF export */}
                       <div className="absolute inset-0" style={{ fontFamily: 'Arial, sans-serif' }}>
                         {/* Nom de l'étudiant - Position: x: pageWidth/2, y: pageHeight*0.45 */}
                         <div 
                           className="absolute transform -translate-x-1/2 -translate-y-1/2"
                           style={{ 
                             left: '50%', 
                             top: '45%',
                             textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                           }}
                         >
                           <h3 
                             className="font-bold text-center"
                             style={{ 
                               fontSize: 'clamp(20px, 3.5vw, 32px)', 
                               color: '#1e1e1e',
                               fontFamily: 'Arial, sans-serif'
                             }}
                           >
                          {certification.studentName}
                        </h3>
                      </div>
                      
                         {/* Nom du certificat - Position: x: pageWidth/2, y: pageHeight*0.6 */}
                         <div 
                           className="absolute transform -translate-x-1/2 -translate-y-1/2"
                           style={{ 
                             left: '50%', 
                             top: '60%',
                             textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                           }}
                         >
                           <p 
                             className="font-bold text-center"
                             style={{ 
                               fontSize: 'clamp(14px, 2.2vw, 20px)', 
                               color: '#323232',
                               fontFamily: 'Arial, sans-serif'
                             }}
                           >
                          {certification.certificateName}
                        </p>
                      </div>
                      
                         {/* Date de délivrance - Position: x: pageWidth*0.25, y: pageHeight*0.8 */}
                         <div 
                           className="absolute transform -translate-x-1/2 -translate-y-1/2"
                           style={{ 
                             left: '25%', 
                             top: '80%',
                             textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                           }}
                         >
                           <p 
                             className="text-center"
                             style={{ 
                               fontSize: 'clamp(8px, 1.2vw, 12px)', 
                               color: '#505050',
                               fontFamily: 'Arial, sans-serif'
                             }}
                           >
                          Délivré le: {certification.issueDate 
                            ? new Date(certification.issueDate).toLocaleDateString('fr-FR') 
                               : new Date().toLocaleDateString('fr-FR')
                             }
                        </p>
                      </div>
                      
                         {/* Formateur - Position: x: pageWidth*0.75, y: pageHeight*0.8 */}
                         <div 
                           className="absolute transform -translate-x-1/2 -translate-y-1/2"
                           style={{ 
                             left: '75%', 
                             top: '80%',
                             textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                           }}
                         >
                           <p 
                             className="text-center"
                             style={{ 
                               fontSize: 'clamp(8px, 1.2vw, 12px)', 
                               color: '#505050',
                               fontFamily: 'Arial, sans-serif'
                             }}
                           >
                          Formateur: {certification.instructorName}
                        </p>
                      </div>
                      
                         {/* Date d'expiration - Position: x: pageWidth*0.5, y: pageHeight*0.75 */}
                         <div 
                           className="absolute transform -translate-x-1/2 -translate-y-1/2"
                           style={{ 
                             left: '50%', 
                             top: '75%',
                             textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                           }}
                         >
                           <p 
                             className="text-center font-bold"
                             style={{ 
                               fontSize: 'clamp(8px, 1.2vw, 12px)', 
                               color: certification.expirationDate === 'never' || !certification.expirationDate ? '#228B22' : '#505050',
                               fontFamily: 'Arial, sans-serif'
                             }}
                           >
                          {certification.expirationDate === 'never' || !certification.expirationDate 
                            ? '∞ Valide à vie' 
                            : `Expire le: ${new Date(certification.expirationDate).toLocaleDateString('fr-FR')}`
                          }
                        </p>
                      </div>
                      
                         {/* Score - Position: x: pageWidth*0.85, y: pageHeight*0.3 */}
                      {certification.score && (
                           <div 
                             className="absolute transform -translate-x-1/2 -translate-y-1/2"
                             style={{ 
                               left: '85%', 
                               top: '30%',
                               textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                             }}
                           >
                             <p 
                               className="text-center font-bold"
                               style={{ 
                                 fontSize: 'clamp(10px, 1.6vw, 16px)', 
                                 color: '#1e1e1e',
                                 fontFamily: 'Arial, sans-serif'
                               }}
                             >
                               Score: {Math.round(certification.score)}/100
                          </p>
                        </div>
                      )}

                         {/* ID du certificat - Position: x: pageWidth/2, y: pageHeight*0.95 */}
                         {certification.id && (
                           <div 
                             className="absolute transform -translate-x-1/2 -translate-y-1/2"
                             style={{ 
                               left: '50%', 
                               top: '95%',
                               textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                             }}
                           >
                             <p 
                               className="text-center"
                               style={{ 
                                 fontSize: 'clamp(6px, 0.8vw, 8px)', 
                                 color: '#787878',
                                 fontFamily: 'Arial, sans-serif'
                               }}
                             >
                               ID: {certification.id}
                             </p>
                           </div>
                         )}
                    </div>
                  </div>
                ) : (
                     // Template en cours de chargement
                     <div className="flex items-center justify-center h-48 bg-gray-100">
                       <div className="text-center">
                         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                         <p className="text-gray-600">Chargement de votre template...</p>
                    </div>
                      </div>
                   )
                 ) : (
                   // Aucun template configuré
                   <div className="flex items-center justify-center h-48 bg-gray-50">
                     <div className="text-center p-6">
                       <div className="mb-4">
                         <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                         </svg>
                        </div>
                       <h3 className="text-lg font-medium text-gray-900 mb-2">Template requis</h3>
                       <p className="text-gray-600 mb-4">Aucun template de certificat n'est configuré. Veuillez en uploader un dans les paramètres.</p>
                       <button 
                         onClick={() => window.location.href = '/settings/certifications'}
                         className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                       >
                         Aller aux paramètres
                       </button>
                        </div>
                      </div>
                )}
              </div>
            </div>
            
            {/* Message d'info */}
            <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-4 border border-slate-200 dark:border-slate-600">
              <p className="text-slate-700 dark:text-slate-300 text-sm text-center">
                {customTemplate ? 
                   `✨ Aperçu avec votre template personnalisé: ${customTemplate.name} - Le PDF exporté utilisera exactement ce même template avec le même positionnement.` :
                   'Aucun template personnalisé n\'est configuré. Allez dans Paramètres → Certificats pour en ajouter un.'
                }
              </p>
            </div>
            
            {/* Zone de téléchargement et impression */}
            <div className="space-y-4">
              {downloadState === 'idle' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={handleDownload}
                    className={`py-3 rounded-xl bg-gradient-to-r ${themeColor} text-white font-medium flex items-center justify-center space-x-2 hover:opacity-90 transition-all`}
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Télécharger PDF</span>
                  </button>
                  
                  <button
                    onClick={handlePrint}
                    className="py-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 font-medium flex items-center justify-center space-x-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-600"
                  >
                    <PrinterIcon className="h-5 w-5" />
                    <span>Imprimer</span>
                </button>
                </div>
              )}
              
              {downloadState === 'downloading' && (
                <div className="space-y-2">
                  <div className="bg-slate-50 dark:bg-slate-800/80 h-10 rounded-xl overflow-hidden">
                    <div 
                      className={`h-full bg-gradient-to-r ${themeColor} flex items-center justify-center text-xs text-white font-medium transition-all duration-300`} 
                      style={{ width: `${loadingProgress}%` }}
                    >
                      {Math.round(loadingProgress)}%
                    </div>
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 text-sm text-center">Préparation du certificat...</p>
                </div>
              )}
              
              {downloadState === 'success' && (
                <div className="space-y-3">
                <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4 flex items-center space-x-3">
                  <div className="bg-green-500/20 p-2 rounded-full">
                    <CheckCircleIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-green-400 font-medium">Téléchargement réussi!</h4>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Le certificat a été téléchargé avec succès</p>
                  </div>
                  </div>
                  
                  {/* Option d'impression après téléchargement */}
                  <button
                    onClick={handlePrint}
                    className="w-full py-2 rounded-xl bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 font-medium flex items-center justify-center space-x-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all border border-slate-200 dark:border-slate-600"
                  >
                    <PrinterIcon className="h-4 w-4" />
                    <span>Imprimer maintenant</span>
                  </button>
                </div>
              )}
              
              {downloadState === 'error' && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                  <p className="text-red-400 text-center">Une erreur est survenue. Veuillez réessayer.</p>
                  <button
                    onClick={handleDownload}
                    className="mt-2 w-full py-2 rounded-lg bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    Réessayer
                  </button>
                </div>
              )}
            </div>
            
            {/* Détails supplémentaires */}
            <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-4 border border-slate-200 dark:border-slate-600">
              <h4 className="font-medium text-slate-900 dark:text-slate-50 mb-2">Détails du certificat</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600 dark:text-slate-400">ID Certificat</p>
                  <p className="text-slate-900 dark:text-slate-50 font-mono">{certification.id}</p>
                </div>
                <div>
                  <p className="text-slate-600 dark:text-slate-400">ID Étudiant</p>
                  <p className="text-slate-900 dark:text-slate-50 font-mono">{certification.studentId}</p>
                </div>
                <div>
                  <p className="text-slate-600 dark:text-slate-400">Type</p>
                  <p className="text-slate-900 dark:text-slate-50">{certificateTypeLabel}</p>
                </div>
                <div>
                  <p className="text-slate-600 dark:text-slate-400">Statut</p>
                  <p className="text-slate-900 dark:text-slate-50">
                    {certification.status === 'issued' ? 'Délivré' : 'En attente'}
                  </p>
                </div>
                {certification.score && (
                  <div>
                    <p className="text-slate-600 dark:text-slate-400">Score</p>
                    <p className="text-slate-900 dark:text-slate-50">{certification.score}/100</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default CertificatePreviewModal;
